import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Globe2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRegulatoryLinks } from '@/hooks/use-regulatory-links';

export function DatabasesPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: regulatoryLinks, isLoading } = useRegulatoryLinks();

  const filteredLinks = useMemo(() => {
    if (!regulatoryLinks)
      return [];
    if (!searchQuery)
      return regulatoryLinks;

    const query = searchQuery.toLowerCase();
    return regulatoryLinks.filter((link) => {
      return (
        link.countryByCountry?.name.toLowerCase().includes(query)
        || link.purpose?.toLowerCase().includes(query)
        || link.url.toLowerCase().includes(query)
        || link.notes?.toLowerCase().includes(query)
      );
    });
  }, [regulatoryLinks, searchQuery]);

  return (
    <div className="flex h-full max-h-[600px] flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b p-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search databases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setSearchQuery('')}
          className="gap-2"
        >
          <RotateCcw className="size-4" />
          Clear
        </Button>
      </div>
      <ScrollArea className="h-[calc(600px-56px)]">
        <div className="p-4">
          {isLoading
            ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading databases...
                </div>
              )
            : (filteredLinks.length === 0
                ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No databases found
                    </div>
                  )
                : (
                    filteredLinks.map((link) => (
                      <div
                        key={link.id}
                        className="mb-4 rounded-lg border bg-card p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        <div className="min-w-0 space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Globe2 className="size-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  {link.countryByCountry?.name}
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-muted-foreground">
                                {link.purpose}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Calendar className="size-3" />
                              <span>
                                {format(new Date(link.updated_at), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                          {link.notes && (
                            <div className="border-t pt-2">
                              <p className="whitespace-pre-line text-sm text-muted-foreground">
                                {link.notes}
                              </p>
                            </div>
                          )}
                          <div className="border-t pt-2">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={link.url}
                              className="flex items-center gap-2 text-sm text-primary hover:underline"
                            >
                              <Globe2 className="size-3" />
                              {link.url.length > 50
                                ? `${link.url.slice(0, 50)}...`
                                : link.url}
                            </a>
                          </div>
                        </div>
                      </div>
                    ))
                  ))}
        </div>
      </ScrollArea>
    </div>
  );
}
